import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, ExternalLink, Video, Calendar, MapPin, Lock, Unlock, Download, Copy, Mail, RefreshCw, UserCheck } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import * as QRCode from 'qrcode';
import { useEvent } from '../hooks/useEvents';
import { useVideos } from '../hooks/useVideos';
import { deleteEvent, updateEvent } from '../hooks/useEvents';
import { formatDate } from '../utils/formatters';
import { EVENT_TYPES } from '../utils/constants';
import logger from '../utils/logger';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorMessage from '../components/shared/ErrorMessage';
import EventForm from '../components/admin/EventForm';
import VideoCard from '../components/admin/VideoCard';
import EmailModal from '../components/admin/EmailModal';
import { sendClientSetupEmail } from '../services/emailService';
import { useNotification } from '../contexts/NotificationContext';
import stoneGoldBg from '../assets/images/stone-gold-black-bg.png';

const AdminEventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { event, loading: eventLoading, error: eventError } = useEvent(eventId, true);
  const { videos, loading: videosLoading, error: videosError } = useVideos(eventId, true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTogglingPrivacy, setIsTogglingPrivacy] = useState(false);
  const [isResendingSetup, setIsResendingSetup] = useState(false);
  const [localIsPublic, setLocalIsPublic] = useState(undefined);
  const { showSuccess, showError } = useNotification();

  // Generate URLs
  const kioskUrl = `${window.location.origin}/kiosk/${eventId}`;
  const galleryUrl = `${window.location.origin}/gallery/${eventId}`;
  const uploadUrl = `${window.location.origin}/upload/${eventId}`;

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${event?.eventName}"? This will permanently delete all ${videos?.length || 0} videos associated with this event. This action cannot be undone.`)) {
      return;
    }

    setIsDeleting(true);
    const result = await deleteEvent(eventId);

    if (result.success) {
      navigate('/admin/dashboard');
    } else {
      showError(`Error deleting event: ${result.error}`);
      setIsDeleting(false);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    showSuccess('Link copied to clipboard!');
  };

  // Download QR code as print-ready image with event name and purpose
  const downloadQRCode = (url, title, purpose) => {
    // Create a temporary canvas
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const size = 400;
    const padding = 60;
    const qrSize = 280;

    // Set canvas size (extra space for text at bottom)
    canvas.width = size;
    canvas.height = size + 80;

    // Fill background
    ctx.fillStyle = '#1A1A1A';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Generate QR code
    const tempQR = document.createElement('canvas');
    QRCode.toCanvas(tempQR, url, {
      width: qrSize,
      margin: 0,
      color: {
        dark: '#D4AF37',
        light: '#1A1A1A'
      },
      errorCorrectionLevel: 'H'
    }, (error) => {
      if (error) {
        logger.error('QR Code generation error:', error);
        showError('Failed to generate QR code. Please try again.');
        return;
      }

      // Draw QR code centered
      const qrX = (size - qrSize) / 2;
      const qrY = padding;
      ctx.drawImage(tempQR, qrX, qrY, qrSize, qrSize);

      // Add event name
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 20px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(event.eventName, size / 2, qrY + qrSize + 30);

      // Add purpose (title)
      ctx.fillStyle = '#D4AF37';
      ctx.font = '16px system-ui, -apple-system, sans-serif';
      ctx.fillText(title, size / 2, qrY + qrSize + 55);

      // Add description
      ctx.fillStyle = '#888888';
      ctx.font = '12px system-ui, -apple-system, sans-serif';
      ctx.fillText(purpose, size / 2, qrY + qrSize + 75);

      // Download
      canvas.toBlob((blob) => {
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${event.eventName.replace(/\s+/g, '_')}_${title.replace(/\s+/g, '_')}.png`;
        link.href = downloadUrl;
        link.click();
        URL.revokeObjectURL(downloadUrl);
      });
    });
  };

  const handlePrivacyToggle = async () => {
    if (!event || isTogglingPrivacy) return;

    const currentIsPublic = localIsPublic ?? event.isPublic ?? true;
    const newIsPublic = !currentIsPublic;

    setLocalIsPublic(newIsPublic);
    setIsTogglingPrivacy(true);

    const result = await updateEvent(eventId, {
      isPublic: newIsPublic
    });

    if (!result.success) {
      setLocalIsPublic(currentIsPublic);
      showError(`Error updating privacy: ${result.error}`);
    }

    setIsTogglingPrivacy(false);
  };

  const handleResendSetupEmail = async () => {
    if (isResendingSetup) return;

    setIsResendingSetup(true);
    const result = await sendClientSetupEmail(eventId);
    if (result.success) {
      showSuccess(`Setup email sent to ${event.clientEmail}`);
    } else {
      showError(`Failed to send setup email: ${result.error}`, {
        action: {
          label: 'Retry',
          onClick: () => handleResendSetupEmail(),
        },
      });
    }
    setIsResendingSetup(false);
  };

  useEffect(() => {
    if (event) {
      setLocalIsPublic(event.isPublic ?? true);
    }
  }, [event?.id, event?.isPublic]);

  const getEventTypeLabel = () => {
    if (!event) return '';
    const type = EVENT_TYPES.find(t => t.value === event.eventType);
    return type ? type.label : event.eventType;
  };

  if (eventLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-950">
        <LoadingSpinner message="Loading event..." />
      </div>
    );
  }

  if (eventError || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-neutral-950">
        <ErrorMessage
          message={eventError || 'Event not found'}
          onRetry={() => navigate('/admin/dashboard')}
        />
      </div>
    );
  }

  const eventDateFormatted = event.eventDate?.toDate
    ? formatDate(event.eventDate.toDate())
    : formatDate(new Date(event.eventDate));
  const isPublic = localIsPublic ?? event.isPublic ?? true;

  return (
    <div
      className="min-h-screen p-6 bg-cover bg-center bg-fixed"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0.6), rgba(0,0,0,0.70)), url(${stoneGoldBg})`
      }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="drop-shadow-lg">Back to Dashboard</span>
        </button>

        {/* Header Section */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-5xl font-bold text-white mb-4 drop-shadow-[0_4px_8px_rgba(0,0,0,0.9)]">
              {event.eventName}
            </h1>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-gray-300">
                <Calendar className="w-5 h-5 text-gold" />
                <span>{eventDateFormatted}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <MapPin className="w-5 h-5 text-gold" />
                <span>{event.location}</span>
              </div>
              <span className="px-4 py-1.5 bg-gold/20 border border-gold/40 text-gold rounded-full text-sm font-semibold">
                {getEventTypeLabel()}
              </span>
            </div>
          </div>

          <div className="flex w-full md:w-auto flex-col sm:flex-row gap-3">
            <button
              onClick={() => setShowEditForm(true)}
              className="w-full sm:w-auto px-6 py-3 bg-transparent border-2 border-gold hover:bg-gold/10 text-gold font-semibold rounded-lg transition-all flex items-center justify-center gap-2 min-h-[48px] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-slate"
            >
              <Edit className="w-4 h-4" />
              Edit
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="w-full sm:w-auto px-6 py-3 bg-crimson hover:bg-crimson/90 text-white font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 min-h-[48px] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-slate"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>

        {/* Stats & Primary Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Stat Card - Upload Count */}
          <div className="bg-neutral-900/50 backdrop-blur-sm border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gold/10 rounded-lg">
                <Video className="w-8 h-8 text-gold" />
              </div>
              <div>
                <p className="text-4xl font-bold text-white">{videos?.length || 0}</p>
                <p className="text-sm text-gray-400">Total Uploads</p>
              </div>
            </div>
          </div>

          {/* Primary Action - Open Kiosk */}
          <button
            onClick={() => window.open(kioskUrl, '_blank')}
            className="bg-neutral-900/50 backdrop-blur-sm border border-white/10 hover:border-gold/50 rounded-xl p-6 transition-all group"
          >
            <div className="flex items-center justify-between">
              <div className="text-left">
                <p className="text-white font-semibold mb-1">Open Kiosk</p>
                <p className="text-gray-400 text-sm">Recording Mode</p>
              </div>
              <ExternalLink className="w-6 h-6 text-gold group-hover:scale-110 transition-transform" />
            </div>
          </button>

          {/* Primary Action - View Gallery */}
          <button
            onClick={() => window.open(galleryUrl, '_blank')}
            className="bg-gold hover:bg-gold-light rounded-xl p-6 transition-all group shadow-lg shadow-gold/20"
          >
            <div className="flex items-center justify-between">
              <div className="text-left">
                <p className="text-dark-slate font-bold mb-1">View Gallery</p>
                <p className="text-dark-slate/80 text-sm">{isPublic ? 'Public View' : 'Private Access'}</p>
              </div>
              <ExternalLink className="w-6 h-6 text-dark-slate group-hover:scale-110 transition-transform" />
            </div>
          </button>
        </div>

        {/* Privacy Toggle */}
        <div className="bg-neutral-900/50 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div className="flex items-center gap-4">
              <div
                className={`p-3 rounded-lg transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                  isPublic ? 'bg-green-500/10' : 'bg-blue-500/10'
                }`}
              >
                <div className="relative w-6 h-6">
                  <Unlock
                    className={`absolute inset-0 w-6 h-6 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isPublic
                        ? 'text-green-500 opacity-100 scale-100 rotate-0'
                        : 'text-green-500 opacity-0 scale-75 -rotate-12'
                    }`}
                  />
                  <Lock
                    className={`absolute inset-0 w-6 h-6 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isPublic
                        ? 'text-blue-500 opacity-0 scale-75 rotate-12'
                        : 'text-blue-500 opacity-100 scale-100 rotate-0'
                    }`}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-xl font-bold text-white">Gallery Access</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    isPublic
                      ? 'bg-green-500/20 border border-green-500/40 text-green-400'
                      : 'bg-blue-500/20 border border-blue-500/40 text-blue-400'
                  } transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]`}>
                    <span className="relative block h-5 w-[58px] overflow-hidden">
                      <span
                        className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isPublic ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                        }`}
                      >
                        Public
                      </span>
                      <span
                        className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isPublic ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
                        }`}
                      >
                        Private
                      </span>
                    </span>
                  </span>
                </div>
                <div className="grid">
                  <p
                    className={`col-start-1 row-start-1 text-gray-400 text-sm transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isPublic ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
                    }`}
                  >
                    Anyone with the link can view and upload videos
                  </p>
                  <p
                    className={`col-start-1 row-start-1 text-gray-400 text-sm transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isPublic ? 'opacity-0 translate-y-1' : 'opacity-100 translate-y-0'
                    }`}
                  >
                    Only {event.hostEmail || 'authorized users'} can access the gallery
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handlePrivacyToggle}
              disabled={isTogglingPrivacy}
              className={`group relative isolate overflow-hidden w-full lg:w-auto lg:min-w-[210px] px-6 py-3 min-h-[48px] rounded-lg font-semibold transition-[transform,box-shadow,background-color,border-color,color] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99] flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-slate ${
                isPublic
                  ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400 hover:bg-blue-500/30'
                  : 'bg-green-500/20 border border-green-500/40 text-green-400 hover:bg-green-500/30'
              }`}
            >
              <span className="pointer-events-none absolute inset-0 bg-white/[0.04] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <span className="relative z-10 flex items-center justify-center gap-2">
                <span className="relative w-4 h-4">
                  <Lock
                    className={`absolute inset-0 w-4 h-4 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isPublic ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-75 -rotate-12'
                    }`}
                  />
                  <Unlock
                    className={`absolute inset-0 w-4 h-4 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isPublic ? 'opacity-0 scale-75 rotate-12' : 'opacity-100 scale-100 rotate-0'
                    }`}
                  />
                </span>
                <span className="relative h-5 w-[112px] overflow-hidden">
                  <span
                    className={`absolute inset-0 flex items-center justify-start transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isPublic ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                    }`}
                  >
                    Make Private
                  </span>
                  <span
                    className={`absolute inset-0 flex items-center justify-start transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                      isPublic ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
                    }`}
                  >
                    Make Public
                  </span>
                </span>
              </span>
            </button>
          </div>
        </div>

        {/* Client Access Status */}
        {event.clientEmail && (
          <div className="bg-neutral-900/50 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
              <div className="flex items-center gap-4 min-w-0">
                <div className={`p-3 rounded-lg ${event.clientSetupStatus === 'active' ? 'bg-green-500/10' : 'bg-gold/10'}`}>
                  <UserCheck className={`w-6 h-6 ${event.clientSetupStatus === 'active' ? 'text-green-500' : 'text-gold'}`} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-xl font-bold text-white">Client Access</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      event.clientSetupStatus === 'active'
                        ? 'bg-green-500/20 border border-green-500/40 text-green-400'
                        : 'bg-gold/20 border border-gold/40 text-gold'
                    }`}>
                      {event.clientSetupStatus === 'active' ? 'Active' : 'Pending Setup'}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm truncate">
                    {event.clientEmail}
                  </p>
                </div>
              </div>

              {event.clientSetupStatus !== 'active' && (
                <button
                  onClick={handleResendSetupEmail}
                  disabled={isResendingSetup}
                  className="group relative isolate overflow-hidden whitespace-nowrap w-full lg:w-auto lg:min-w-[264px] px-6 py-3 min-h-[48px] bg-gold/20 border border-gold/40 text-gold hover:bg-gold/30 rounded-lg font-semibold transition-[background-color,border-color,color,box-shadow,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99] flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 focus-visible:ring-offset-2 focus-visible:ring-offset-dark-slate"
                >
                  <span className="pointer-events-none absolute inset-0 bg-white/[0.04] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <span className="relative w-4 h-4">
                      <RefreshCw
                        className={`absolute inset-0 w-4 h-4 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isResendingSetup ? 'opacity-100 scale-100 rotate-0 animate-spin' : 'opacity-100 scale-100 rotate-0'
                        }`}
                      />
                    </span>
                    <span className="relative inline-grid h-5 overflow-hidden">
                      <span className="invisible col-start-1 row-start-1 whitespace-nowrap">
                        Resend Setup Email
                      </span>
                      <span
                        className={`col-start-1 row-start-1 flex items-center whitespace-nowrap transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isResendingSetup ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'
                        }`}
                      >
                        Resend Setup Email
                      </span>
                      <span
                        className={`col-start-1 row-start-1 flex items-center whitespace-nowrap transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                          isResendingSetup ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                        }`}
                      >
                        Sending...
                      </span>
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* QR Codes - De-emphasized */}
        <div className="bg-neutral-900/30 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-8">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <span>Share Links</span>
            <span className="text-sm text-gray-500 font-normal">(QR Codes)</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Kiosk QR */}
            <div className="text-center">
              <div className="inline-block bg-neutral-900 p-4 rounded-xl border-2 border-gold/30 mb-3">
                <QRCodeSVG
                  value={kioskUrl}
                  size={140}
                  level="H"
                  fgColor="#D4AF37"
                  bgColor="#1A1A1A"
                />
              </div>
              <p className="text-gray-300 font-medium mb-2">Kiosk Mode</p>
              <p className="text-xs text-gray-500 mb-3">Record videos in real-time</p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => copyToClipboard(kioskUrl)}
                  className="text-sm text-gold hover:text-gold-light flex items-center gap-1 justify-center mx-auto"
                >
                  <Copy className="w-3 h-3" />
                  Copy Link
                </button>
                <button
                  onClick={() => downloadQRCode(kioskUrl, 'Kiosk Mode', 'Record videos in real-time')}
                  className="text-sm text-white/70 hover:text-white flex items-center gap-1 justify-center mx-auto"
                >
                  <Download className="w-3 h-3" />
                  Download QR
                </button>
              </div>
            </div>

            {/* Guest Upload QR */}
            <div className="text-center">
              <div className="inline-block bg-neutral-900 p-4 rounded-xl border-2 border-gold/30 mb-3">
                <QRCodeSVG
                  value={uploadUrl}
                  size={140}
                  level="H"
                  fgColor="#D4AF37"
                  bgColor="#1A1A1A"
                />
              </div>
              <p className="text-gray-300 font-medium mb-2">Guest Upload</p>
              <p className="text-xs text-gray-500 mb-3">Upload pre-recorded videos</p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => copyToClipboard(uploadUrl)}
                  className="text-sm text-gold hover:text-gold-light flex items-center gap-1 justify-center mx-auto"
                >
                  <Copy className="w-3 h-3" />
                  Copy Link
                </button>
                <button
                  onClick={() => downloadQRCode(uploadUrl, 'Guest Upload', 'Upload pre-recorded videos')}
                  className="text-sm text-white/70 hover:text-white flex items-center gap-1 justify-center mx-auto"
                >
                  <Download className="w-3 h-3" />
                  Download QR
                </button>
              </div>
            </div>

            {/* Gallery QR */}
            <div className="text-center">
              <div className="inline-block bg-neutral-900 p-4 rounded-xl border-2 border-gold/30 mb-3">
                <QRCodeSVG
                  value={galleryUrl}
                  size={140}
                  level="H"
                  fgColor="#D4AF37"
                  bgColor="#1A1A1A"
                />
              </div>
              <p className="text-gray-300 font-medium mb-2 flex items-center justify-center gap-2">
                Guest Gallery
                {event.guestPassword && (
                  <Lock className="w-4 h-4 text-gold" />
                )}
              </p>
              <p className="text-xs text-gray-500 mb-3">View all videos</p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => copyToClipboard(galleryUrl)}
                  className="text-sm text-gold hover:text-gold-light flex items-center gap-1 justify-center mx-auto"
                >
                  <Copy className="w-3 h-3" />
                  Copy Link
                </button>
                <button
                  onClick={() => downloadQRCode(galleryUrl, 'Guest Gallery', 'View all videos')}
                  className="text-sm text-white/70 hover:text-white flex items-center gap-1 justify-center mx-auto"
                >
                  <Download className="w-3 h-3" />
                  Download QR
                </button>
              </div>
              {event.guestPassword && (
                <p className="text-xs text-gray-500 mt-2">
                  Password: <span className="text-gold font-mono">{event.guestPassword}</span>
                </p>
              )}
            </div>
          </div>

          {/* Email Share Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setShowEmailModal(true)}
              className="px-8 py-3 bg-gold hover:bg-gold-light text-dark-slate font-semibold rounded-lg transition-all flex items-center gap-3 shadow-lg shadow-gold/20"
            >
              <Mail className="w-5 h-5" />
              <span>Share Gallery via Email</span>
            </button>
          </div>
        </div>

        {/* Videos Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-white drop-shadow-lg">
              Uploads
              {videos && videos.length > 0 && (
                <span className="ml-3 text-xl font-normal text-gray-400">
                  ({videos.length})
                </span>
              )}
            </h2>
          </div>

          {videosLoading && (
            <div className="flex justify-center py-20">
              <LoadingSpinner message="Loading videos..." />
            </div>
          )}

          {videosError && (
            <div className="flex justify-center py-12">
              <ErrorMessage message={videosError} />
            </div>
          )}

          {!videosLoading && !videosError && (
            <>
              {videos.length === 0 ? (
                <div className="bg-neutral-900/30 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center">
                  <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Video className="w-10 h-10 text-gold" />
                  </div>
                  <p className="text-gray-300 text-lg mb-6">
                    No videos yet. Share the kiosk link to start collecting messages!
                  </p>
                  <button
                    onClick={() => window.open(kioskUrl, '_blank')}
                    className="btn-primary"
                  >
                    Open Kiosk Mode
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {videos.map(video => (
                    <VideoCard key={video.id} video={video} eventId={eventId} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Event Form Modal */}
      {showEditForm && (
        <EventForm
          existingEvent={event}
          onClose={() => setShowEditForm(false)}
          onSuccess={() => {
            logger.log('Event updated');
          }}
        />
      )}

      {/* Email Modal */}
      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        eventData={{ ...event, id: eventId }}
      />
    </div>
  );
};

export default AdminEventDetailPage;
